import React, { useEffect, useState, useRef } from "react";
import {
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useUploadArticleImageMutation,
  useGetModulesQuery,
  Article
} from "../../../../store/apis/helpCenter.api";

import {
  Modal,
  ModalFooter,
  ModalButton,
  Input,
  TextArea,
  Button,
  SimpleTooltip,
  Select,
  Badge
} from "../../../common";

import { X, Upload, Image as ImageIcon, Loader2, FileText, Eye, Pencil } from "lucide-react";
import toast from "react-hot-toast";
import { parseHelpCenterMarkdownFile } from "../../../../utils/helpCenterMarkdown";
import { MarkdownContent } from "../../MarkdownContent";

interface ArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  article?: Article | null;
  /** Read-only rendered preview (no form fields) */
  viewMode?: boolean;
  onRequestEdit?: (article: Article) => void;
}

type ArticleWithId = Article & { _id: string };
type EditorTab = "write" | "preview";

const ArticleModal: React.FC<ArticleModalProps> = ({
  isOpen,
  onClose,
  article,
  viewMode = false,
  onRequestEdit,
}) => {
  const { data: modules = [], isLoading: isModulesLoading } = useGetModulesQuery();
  const [createArticle, { isLoading: isCreating }] = useCreateArticleMutation();
  const [updateArticle, { isLoading: isUpdating }] = useUpdateArticleMutation();
  const [uploadImage, { isLoading: isUploading }] = useUploadArticleImageMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mdInputRef = useRef<HTMLInputElement>(null);
  const formId = "article-form-modal";

  const [title, setTitle] = useState("");
  const [moduleCode, setModuleCode] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [content, setContent] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [editorTab, setEditorTab] = useState<EditorTab>("write");

  const isSubmitting = isCreating || isUpdating;
  const isReadOnly = viewMode;

  useEffect(() => {
    if (article) {
      const a = article as ArticleWithId;
      setTitle(a.title || "");
      setModuleCode(a.moduleCode || "");
      setContent(a.content || "");
      setImageUrl(a.imageUrl || "");
      setIsPublished(a.isPublished ?? false);
    } else {
      setTitle("");
      setModuleCode("");
      setContent("");
      setImageUrl("");
      setIsPublished(false);
    }
    setEditorTab(isReadOnly ? "preview" : "write");
  }, [article, isOpen, isReadOnly]);

  const resolveModuleCode = (moduleLabel?: string): string | undefined => {
    if (!moduleLabel?.trim()) return undefined;
    const label = moduleLabel.trim().toLowerCase();
    const exact = modules.find(
      (m) => m.name.toLowerCase() === label || m.code.toLowerCase() === label,
    );
    if (exact) return exact.code;
    const partial = modules.find((m) => m.name.toLowerCase().includes(label));
    return partial?.code;
  };

  const applyMarkdownImport = (raw: string) => {
    const parsed = parseHelpCenterMarkdownFile(raw);
    if (parsed.title && (!title.trim() || !article)) {
      setTitle(parsed.title);
    }
    const code = resolveModuleCode(parsed.moduleLabel);
    if (code) {
      setModuleCode(code);
    } else if (parsed.moduleLabel) {
      toast.error(`Could not match module "${parsed.moduleLabel}". Select it manually.`);
    }
    setContent(parsed.body || raw.trim());
    setEditorTab("preview");
    toast.success("Markdown imported. Body is ready to save.");
  };

  const handleMarkdownFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      applyMarkdownImport(text);
    };
    reader.onerror = () => toast.error("Failed to read markdown file");
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const result = await uploadImage(formData).unwrap();
      if (result.success) {
        setImageUrl(result.data.fileUrl);
        toast.success("Image uploaded successfully");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to upload image");
    }
  };

  const getContentForSave = (): string => {
    const trimmed = content.trim();
    if (!trimmed) return "";
    const parsed = parseHelpCenterMarkdownFile(trimmed);
    return parsed.body.trim() || trimmed;
  };

  const handleSave = async (finalPublished: boolean) => {
    const body = getContentForSave();
    if (!title.trim() || !body || !moduleCode) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const payload: Record<string, unknown> = {
        title: title.trim(),
        content: body,
        moduleCode,
        isPublished: finalPublished,
        imageUrl,
      };

      if (article) {
        const a = article as ArticleWithId;
        await updateArticle({
          id: a._id,
          ...payload,
        }).unwrap();
        toast.success(`Article updated and ${finalPublished ? "published" : "saved as draft"}`);
      } else {
        await createArticle(payload).unwrap();
        toast.success(`Article ${finalPublished ? "created and published" : "saved as draft"}`);
      }

      onClose();
    } catch (error) {
      console.error("Failed to save article:", error);
      toast.error("Failed to save article");
    }
  };

  const modalTitle = isReadOnly
    ? "View Article"
    : article
      ? "Edit Article"
      : "Add New Article";

  const modalHeader = (
    <div className="flex items-center gap-3">
      <span className="text-lg font-bold text-slate-900">{modalTitle}</span>
      {article && (
        <Badge variant={isPublished ? "green" : "gray"} size="small">
          {isPublished ? "Published" : "Draft"}
        </Badge>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalHeader}
      size="4xl"
      maskClosable={false}
      bodyClassName="space-y-8"
      footer={
        isReadOnly ? (
          <ModalFooter>
            <ModalButton variant="secondary" onClick={onClose}>
              Close
            </ModalButton>
            {article && onRequestEdit && (
              <ModalButton
                variant="primary"
                onClick={() => onRequestEdit(article)}
              >
                Edit
              </ModalButton>
            )}
          </ModalFooter>
        ) : (
          <ModalFooter>
            <ModalButton
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </ModalButton>
            <ModalButton
              variant="secondary"
              onClick={() => handleSave(false)}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Draft
            </ModalButton>
            <ModalButton
              variant="primary"
              onClick={() => handleSave(true)}
              loading={isSubmitting}
              disabled={isSubmitting}
              className="px-8"
            >
              Publish
            </ModalButton>
          </ModalFooter>
        )
      }
    >
      {isReadOnly && article ? (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-1">{article.title}</h2>
            <p className="text-xs text-slate-500">
              Module:{" "}
              {modules.find((m) => m.code === article.moduleCode)?.name ||
                article.moduleCode}
            </p>
          </div>
          {article.imageUrl && (
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full max-h-80 object-contain rounded-xl border border-slate-100"
            />
          )}
          <div className="rounded-xl border border-slate-200 bg-white p-6">
            <MarkdownContent content={article.content} />
          </div>
        </div>
      ) : (
        <form id={formId} onSubmit={(e) => e.preventDefault()} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
            <Input
              label="Article Title"
              value={title}
              onChange={(v) => setTitle(v as string)}
              placeholder="e.g., How to mark attendance"
              required
              autoFocus
            />
            <Select
              label="Module"
              value={moduleCode}
              onChange={(val) => setModuleCode(val as string)}
              options={modules.map((m) => ({
                label: m.name,
                value: m.code,
              }))}
              placeholder="Select module"
              isLoading={isModulesLoading}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Cover Image</label>
            <div className="flex flex-col sm:flex-row items-center gap-4 p-4 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 transition-colors hover:border-primary-300">
              {imageUrl ? (
                <div className="relative group w-24 h-16 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0">
                  <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setImageUrl("")}
                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-16 flex items-center justify-center bg-white rounded-lg border border-slate-200 flex-shrink-0">
                  <ImageIcon className="w-6 h-6 text-slate-300" />
                </div>
              )}

              <div className="flex-1 flex flex-col gap-1 items-center sm:items-start overflow-hidden">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <Button
                  variant="ghost"
                  size="small"
                  className="text-primary-600 hover:text-primary-700 hover:bg-primary-50 px-0"
                  icon={
                    isUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )
                  }
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {imageUrl ? "Change Image" : "Upload Image"}
                </Button>
                {imageUrl ? (
                  <SimpleTooltip message={imageUrl}>
                    <p className="text-[10px] text-slate-400 truncate w-full">{imageUrl}</p>
                  </SimpleTooltip>
                ) : (
                  <p className="text-[10px] text-slate-400">JPG, PNG, WEBP (Max 2MB)</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <label className="text-sm font-semibold text-slate-700">
                Article Content <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="file"
                  ref={mdInputRef}
                  className="hidden"
                  accept=".md,.markdown,.txt"
                  onChange={handleMarkdownFileChange}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="small"
                  icon={<FileText className="w-4 h-4" />}
                  onClick={() => mdInputRef.current?.click()}
                >
                  Import .md file
                </Button>
                <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
                  <button
                    type="button"
                    onClick={() => setEditorTab("write")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      editorTab === "write"
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Write
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditorTab("preview")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      editorTab === "preview"
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Preview
                  </button>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-slate-500">
              Paste raw Markdown (content below the <code className="text-xs bg-slate-100 px-1 rounded">---</code> line in your .md file). Front matter (title, module) is applied on import only.
            </p>

            {editorTab === "write" ? (
              <TextArea
                value={content}
                onChange={(v) => setContent(v as string)}
                placeholder="## Who this is for&#10;&#10;Write your guide in Markdown..."
                minRows={14}
                className="font-mono text-xs"
              />
            ) : (
              <div className="min-h-[280px] rounded-xl border border-slate-200 bg-white p-6 overflow-y-auto max-h-[420px]">
                <MarkdownContent content={content || "*Nothing to preview yet.*"} />
              </div>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
};

export default ArticleModal;
